<template>
  <Layout>
    <div class="bloco">
      <div class="cabecalho">
        <div class="row">
          <div class="col-md-11">
            <div class="titulo">Envio de Receituários - Agrodefesa</div>
            <div class="subTitulo">
              Consulte as receitas pendentes por período e envie o XML da NF-e para a Agrodefesa.
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <div class="borda"></div>
          </div>
        </div>
      </div>

      <div class="agrodefesa-panel">
        <div class="row align-items-end">
          <div class="col-md-2">
            <b-form-group label="Data inicial">
              <date-picker
                v-model="filtros.startDate"
                :first-day-of-week="0"
                format="YYYY-MM-DD"
                value-type="format"
                lang="pt-br"
                placeholder="dd/mm/yyyy"
                :class="{ empty: isSubmit && !filtros.startDate }"
              ></date-picker>
            </b-form-group>
          </div>
          <div class="col-md-2">
            <b-form-group label="Data final">
              <date-picker
                v-model="filtros.endDate"
                :first-day-of-week="0"
                format="YYYY-MM-DD"
                value-type="format"
                lang="pt-br"
                placeholder="dd/mm/yyyy"
                :class="{ empty: isSubmit && !filtros.endDate }"
              ></date-picker>
            </b-form-group>
          </div>
          <div class="col-md-8 d-flex flex-wrap">
            <b-button
              variant="primary"
              class="mr-2 mb-3"
              :disabled="isLoading"
              @click="consultar()"
            >
              Consultar
            </b-button>            
          </div>
        </div>
      </div>

      <b-alert
        v-if="resultadoEnvio"
        show
        :variant="resultadoEnvio.falhas ? 'warning' : 'success'"
        class="mt-3"
      >
        <strong>Envio concluído:</strong>
        {{ resultadoEnvio.enviados || 0 }} enviado(s) e
        {{ resultadoEnvio.falhas || 0 }} falha(s).
      </b-alert>

      <div class="mt-3">
        <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
          <h5 class="mb-2">Receitas pendentes</h5>
          <span class="text-muted mb-2">{{ totalPendentes }} pendente(s)</span>
        </div>

        <b-table
          striped
          hover
          responsive
          bordered
          outlined
          small
          :items="receitas"
          :fields="fields"
          :busy="isLoading"
          empty-text="Nenhuma receita pendente encontrada para o período."
          show-empty
        >
          <template v-slot:cell(data_emissao)="row">
            {{ formatDate(row.item.data_emissao) }}
          </template>
          <template v-slot:cell(produtor_nome)="row">
            {{ getNested(row.item, 'produtor', 'nome') }}
          </template>
          <template v-slot:cell(produtor_cpf_cnpj)="row">
            {{ getNested(row.item, 'produtor', 'cpf_cnpj') }}
          </template>
          <template v-slot:cell(propriedade)="row">
            {{ getNested(row.item, 'produtor', 'propriedade') }}
          </template>
          <template v-slot:cell(municipio)="row">
            {{ getNested(row.item, 'produtor', 'municipio') }}
          </template>
          <template v-slot:cell(agronomo_nome)="row">
            {{ getNested(row.item, 'agronomo', 'nome') }}
          </template>
          <template v-slot:cell(comerciante_nome)="row">
            {{ getNested(row.item, 'comerciante', 'nome') }}
          </template>
          <template v-slot:cell(actions)="row">
            <b-button
              size="sm"
              variant="outline-success"
              :disabled="isLoading"
              @click="abrirModalEnvioReceita(row.item)"
            >
              Enviar
            </b-button>
          </template>
          <template v-slot:table-busy>
            <div class="text-center text-muted my-3">
              Carregando receitas pendentes...
            </div>
          </template>
        </b-table>
      </div>

      <loading
        :active.sync="isLoading"
        color="#008e75"
        :can-cancel="false"
      ></loading>

      <b-modal
        v-model="showModalEnvio"
        modal-class="agrodefesa-send-modal"
        dialog-class="agrodefesa-send-dialog"
        title-class="font-18"
        :no-close-on-backdrop="isSending"
        :no-close-on-esc="isSending"
        :hide-header-close="isSending"
        @hidden="resetModalEnvio"
      >
        <template v-slot:modal-title>
          <div class="agrodefesa-modal-title">
            <span class="agrodefesa-modal-icon">XML</span>
            <div>
              <div class="agrodefesa-modal-heading">{{ modalEnvioTitle }}</div>
              <div class="agrodefesa-modal-subtitle">{{ modalEnvioSubtitle }}</div>
            </div>
          </div>
        </template>

        <div v-if="selectedReceitaDetails.length" class="agrodefesa-send-summary">
          <div
            v-for="detail in selectedReceitaDetails"
            :key="detail.label"
            class="agrodefesa-send-summary-item"
          >
            <span>{{ detail.label }}</span>
            <strong>{{ detail.value }}</strong>
          </div>
        </div>

        <b-form>
          <b-form-group label="XML da NF-e" label-class="agrodefesa-upload-label">
            <div
              class="agrodefesa-upload-box"
              :class="{ 'has-file': xmlFile }"
              role="button"
              tabindex="0"
              @click="abrirSeletorXml"
              @keydown.enter.prevent="abrirSeletorXml"
              @keydown.space.prevent="abrirSeletorXml"
            >
              <div class="agrodefesa-upload-icon">XML</div>
              <div class="agrodefesa-upload-copy">
                <strong>{{ xmlFile ? xmlFile.name : 'Selecione o XML da NF-e' }}</strong>
                <span>
                  {{ xmlFile ? formatFileSize(xmlFile.size) : 'Arquivo .xml usado para localizar a chave e os itens da nota.' }}
                </span>
              </div>
              <span class="agrodefesa-upload-status">
                {{ xmlFile ? 'Selecionado' : 'Pendente' }}
              </span>
            </div>
            <input
              ref="xmlFileInput"
              class="agrodefesa-file-input"
              type="file"
              accept=".xml,text/xml,application/xml"
              :disabled="isSending"
              @change="selecionarXml"
            />
          </b-form-group>
        </b-form>

        <div v-if="envioErroModal" class="mt-3">
          <b-alert show variant="danger" class="mb-0">
            {{ envioErroModal }}
          </b-alert>
        </div>

        <div v-if="envioResultadoModal" class="mt-3">
          <b-alert
            show
            :variant="envioResultadoModal.falhas ? 'warning' : 'success'"
            class="mb-3"
          >
            <strong>Retorno da Agrodefesa:</strong>
            {{ envioResultadoModal.enviados || 0 }} enviado(s) e
            {{ envioResultadoModal.falhas || 0 }} falha(s).
          </b-alert>

          <div v-if="envioResultadoModal.ok && envioResultadoModal.ok.length" class="mb-3">
            <h6 class="mb-2">Enviados</h6>
            <b-table
              striped
              small
              responsive
              :items="envioResultadoModal.ok"
              :fields="okFields"
            >
              <template v-slot:cell(response)="row">
                <pre class="agrodefesa-log-json">{{ formatLog(row.item.response) }}</pre>
              </template>
            </b-table>
          </div>

          <div v-if="envioResultadoModal.failed && envioResultadoModal.failed.length">
            <h6 class="mb-2">Falhas</h6>
            <div
              v-for="(falha, index) in envioResultadoModal.failed"
              :key="`${falha.numero || 'falha'}-${index}`"
              class="agrodefesa-failure"
            >
              <div class="d-flex align-items-center justify-content-between mb-2">
                <strong>Receita {{ formatReceitaNumero(falha.numero) }}</strong>
                <b-badge variant="danger">Falha</b-badge>
              </div>

              <ul class="agrodefesa-message-list">
                <li
                  v-for="(message, messageIndex) in getFailureMessages(falha)"
                  :key="messageIndex"
                >
                  {{ message }}
                </li>
              </ul>

              <details v-if="getFailureDetails(falha)" class="mt-2">
                <summary>Detalhes técnicos</summary>
                <pre class="agrodefesa-log-json mt-2">{{ getFailureDetails(falha) }}</pre>
              </details>
            </div>
          </div>
        </div>

        <template v-slot:modal-footer>
          <b-button
            variant="light"
            class="agrodefesa-modal-cancel"
            :disabled="isSending"
            @click="showModalEnvio = false"
          >
            {{ envioResultadoModal || envioErroModal ? 'Fechar' : 'Cancelar' }}
          </b-button>
          <b-button
            variant="success"
            class="agrodefesa-modal-submit"
            :disabled="isSending"
            @click="confirmarEnvio"
          >
            <b-spinner v-if="isSending" small class="mr-1"></b-spinner>
            {{ isSending ? 'Enviando...' : 'Enviar' }}
          </b-button>
        </template>
      </b-modal>
    </div>
  </Layout>
</template>

<script>
import Layout from '@layouts/main'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/locale/pt-br'
import 'vue2-datepicker/index.css'
import agrodefesaService from '@/src/services/api-receituario/agrodefesa'

export default {
  components: { Layout, Loading, DatePicker },
  data() {
    return {
      isLoading: false,
      isSending: false,
      isSubmit: false,
      filtros: {
        startDate: '',
        endDate: '',
      },
      xmlFile: null,
      showModalEnvio: false,
      envioSelecionado: null,
      envioResultadoModal: null,
      envioErroModal: '',
      receitas: [],
      totalPendentes: 0,
      resultadoEnvio: null,
      fields: [
        { key: 'numero_receita', label: 'Número da receita' },
        { key: 'data_emissao', label: 'Data de emissão' },
        { key: 'produtor_nome', label: 'Produtor' },
        { key: 'produtor_cpf_cnpj', label: 'CPF/CNPJ do produtor' },
        { key: 'propriedade', label: 'Propriedade' },
        { key: 'municipio', label: 'Município' },
        { key: 'agronomo_nome', label: 'Agrônomo' },
        { key: 'comerciante_nome', label: 'Comerciante' },
        { key: 'actions', label: '' },
      ],
      okFields: [
        { key: 'numero', label: 'Receita' },
        { key: 'codigo_agrodefesa', label: 'Código Agrodefesa' },
        { key: 'response', label: 'Log' },
      ],
    }
  },
  computed: {
    modalEnvioTitle() {
      return this.envioSelecionado && this.envioSelecionado.numeroReceita
        ? `Enviar receita ${this.envioSelecionado.numeroReceita}`
        : 'Enviar período'
    },
    modalEnvioSubtitle() {
      return this.envioSelecionado && this.envioSelecionado.numeroReceita
        ? 'Envio individual para Agrodefesa'
        : 'Envio das receitas pendentes do período'
    },
    selectedReceita() {
      if (!this.envioSelecionado || !this.envioSelecionado.numeroReceita) {
        return null
      }

      return this.receitas.find(
        (receita) =>
          String(receita.numero_receita || '').trim() ===
          String(this.envioSelecionado.numeroReceita || '').trim()
      )
    },
    selectedReceitaDetails() {
      if (!this.selectedReceita) {
        return [
          { label: 'Período', value: `${this.filtros.startDate} até ${this.filtros.endDate}` },
          { label: 'Pendentes', value: `${this.totalPendentes} receita(s)` },
        ]
      }

      return [
        { label: 'Produtor', value: this.getNested(this.selectedReceita, 'produtor', 'nome') },
        { label: 'Município', value: this.getNested(this.selectedReceita, 'produtor', 'municipio') },
        { label: 'Emissão', value: this.formatDate(this.selectedReceita.data_emissao) },
      ]
    },
  },
  watch: {
    filtros: {
      handler() {
        this.isSubmit = false
      },
      deep: true,
    },
  },
  methods: {
    getToken() {
      return this.$store.state.auth.currentUser.token
    },
    getKey() {
      try {
        const configLocal = JSON.parse(window.localStorage.getItem('configuracao')) || {}
        return configLocal.key || ''
      } catch (error) {
        return ''
      }
    },
    getErrorMessage(error, fallback) {
      return error &&
        error.response &&
        error.response.data &&
        (error.response.data.message || error.response.data.msg || error.response.data.error)
        ? error.response.data.message || error.response.data.msg || error.response.data.error
        : fallback
    },
    validarPeriodo() {
      if (!this.filtros.startDate || !this.filtros.endDate) {
        this.isSubmit = true
        this.$bvToast.toast('Informe Data inicial e Data final.', {
          variant: 'warning',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        return false
      }

      return true
    },
    validarXml() {
      if (!this.xmlFile) {
        this.$bvToast.toast('Anexe o XML da NF-e.', {
          variant: 'warning',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        return false
      }

      return true
    },
    consultar(preserveResultado, silent) {
      if (!this.validarPeriodo()) {
        return
      }

      if (!silent) {
        this.isLoading = true
      }

      if (!preserveResultado) {
        this.resultadoEnvio = null
      }

      return agrodefesaService
        .listarPendentes(this.getToken(), this.getKey(), this.filtros)
        .then((response) => {
          const data = response.data || {}
          this.receitas = Array.isArray(data.receitas) ? data.receitas : []
          this.totalPendentes = data.total !== undefined ? data.total : this.receitas.length
        })
        .catch((error) => {
          this.receitas = []
          this.totalPendentes = 0
          this.$bvToast.toast(
            this.getErrorMessage(error, 'Não foi possível consultar as receitas pendentes.'),
            {
              variant: 'danger',
              solid: false,
              noCloseButton: true,
              toaster: 'b-toaster-top-center',
            }
          )
        })
        .finally(() => {
          if (!silent) {
            this.isLoading = false
          }
        })
    },
    abrirModalEnvioPeriodo() {
      if (!this.validarPeriodo()) {
        return
      }

      this.xmlFile = null
      this.envioResultadoModal = null
      this.envioErroModal = ''
      this.envioSelecionado = {
        startDate: this.filtros.startDate,
        endDate: this.filtros.endDate,
      }
      this.showModalEnvio = true
    },
    abrirModalEnvioReceita(receita) {
      this.xmlFile = null
      this.envioResultadoModal = null
      this.envioErroModal = ''
      this.envioSelecionado = {
        numeroReceita: receita.numero_receita,
      }
      this.showModalEnvio = true
    },
    abrirSeletorXml() {
      if (this.isSending || !this.$refs.xmlFileInput) {
        return
      }

      this.$refs.xmlFileInput.click()
    },
    selecionarXml(event) {
      const files = event && event.target ? event.target.files : null
      this.xmlFile = files && files.length ? files[0] : null
    },
    confirmarEnvio() {
      if (!this.validarXml() || !this.envioSelecionado) {
        return
      }

      this.enviar({
        ...this.envioSelecionado,
        file: this.xmlFile,
      })
    },
    enviar(payload) {
      this.isSending = true
      this.envioResultadoModal = null
      this.envioErroModal = ''

      agrodefesaService
        .enviar(this.getToken(), this.getKey(), payload)
        .then((response) => {
          this.resultadoEnvio = response.data || {}
          this.envioResultadoModal = this.resultadoEnvio
          this.$bvToast.toast('Envio para Agrodefesa concluído.', {
            variant: this.resultadoEnvio.falhas ? 'warning' : 'success',
            solid: false,
            noCloseButton: true,
            toaster: 'b-toaster-top-center',
          })

          if (this.filtros.startDate && this.filtros.endDate) {
            return this.consultar(true, true)
          }

          return null
        })
        .catch((error) => {
          this.envioErroModal = this.getErrorMessage(
            error,
            'Não foi possível enviar para Agrodefesa.'
          )
          this.$bvToast.toast(
            this.envioErroModal,
            {
              variant: 'danger',
              solid: false,
              noCloseButton: true,
              toaster: 'b-toaster-top-center',
            }
          )
        })
        .finally(() => {
          this.isSending = false
        })
    },
    resetModalEnvio() {
      this.xmlFile = null
      this.envioSelecionado = null
      this.envioResultadoModal = null
      this.envioErroModal = ''

      if (this.$refs.xmlFileInput) {
        this.$refs.xmlFileInput.value = ''
      }
    },
    formatLog(value) {
      if (!value) {
        return '-'
      }

      if (typeof value === 'string') {
        return value
      }

      return JSON.stringify(value, null, 2)
    },
    formatFileSize(size) {
      if (!size) {
        return 'Arquivo selecionado'
      }

      if (size < 1024) {
        return `${size} B`
      }

      if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(1)} KB`
      }

      return `${(size / 1024 / 1024).toFixed(1)} MB`
    },
    formatReceitaNumero(value) {
      return value ? String(value).trim() : '-'
    },
    getFailureError(falha) {
      return falha && falha.error ? falha.error : null
    },
    getFailureMessages(falha) {
      const error = this.getFailureError(falha)

      if (!error) {
        return ['Erro não informado pela Agrodefesa.']
      }

      if (typeof error === 'string') {
        return [error]
      }

      if (Array.isArray(error.messages) && error.messages.length) {
        return error.messages
      }

      if (
        error.response &&
        Array.isArray(error.response.messages) &&
        error.response.messages.length
      ) {
        return error.response.messages
      }

      if (error.message) {
        return [error.message]
      }

      return ['Falha retornada pela Agrodefesa.']
    },
    getFailureDetails(falha) {
      const error = this.getFailureError(falha)

      if (!error || typeof error === 'string') {
        return ''
      }

      return this.formatLog(error)
    },
    formatDate(value) {
      if (!value) {
        return '-'
      }

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return value
      }

      return date.toLocaleDateString('pt-BR')
    },
    getNested(item, parent, key) {
      return item && item[parent] && item[parent][key] ? item[parent][key] : '-'
    },
  },
}
</script>

<style>
.empty {
  border: 1px solid red;
}

.agrodefesa-panel {
  margin-bottom: 1rem;
}

.agrodefesa-send-dialog {
  max-width: 860px;
}

.agrodefesa-send-modal .modal-content {
  border: 0;
  border-radius: 10px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.agrodefesa-send-modal .modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #eef2f7;
  background: #fbfcfe;
}

.agrodefesa-send-modal .modal-body {
  padding: 22px 24px;
}

.agrodefesa-send-modal .modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #eef2f7;
  background: #fbfcfe;
}

.agrodefesa-modal-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.agrodefesa-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #e8f8f4;
  color: #008e75;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0;
}

.agrodefesa-modal-heading {
  color: #343a40;
  font-weight: 600;
  line-height: 1.2;
}

.agrodefesa-modal-subtitle {
  margin-top: 3px;
  color: #6c757d;
  font-size: 13px;
  font-weight: 400;
}

.agrodefesa-send-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}

.agrodefesa-send-summary-item {
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #e8edf3;
  border-radius: 6px;
  background: #f8fafc;
}

.agrodefesa-send-summary-item span {
  display: block;
  color: #7a8391;
  font-size: 12px;
  line-height: 1.2;
}

.agrodefesa-send-summary-item strong {
  display: block;
  margin-top: 4px;
  color: #303642;
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agrodefesa-upload-label {
  color: #4a5260;
  font-weight: 600;
}

.agrodefesa-upload-box {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 86px;
  padding: 16px 18px;
  border: 1px dashed #adc3d8;
  border-radius: 8px;
  background: #f8fbfd;
  cursor: pointer;
  outline: 0;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.agrodefesa-upload-box:hover,
.agrodefesa-upload-box:focus {
  border-color: #008e75;
  background: #f6fbfa;
  box-shadow: 0 0 0 0.2rem rgba(0, 142, 117, 0.12);
}

.agrodefesa-upload-box.has-file {
  border-color: #008e75;
  background: #f4fbf9;
  box-shadow: inset 0 0 0 1px rgba(0, 142, 117, 0.08);
}

.agrodefesa-upload-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #e8f8f4;
  color: #008e75;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
}

.agrodefesa-upload-copy {
  min-width: 0;
  flex: 1 1 auto;
}

.agrodefesa-upload-copy strong,
.agrodefesa-upload-copy span {
  display: block;
}

.agrodefesa-upload-copy strong {
  color: #303642;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agrodefesa-upload-copy span {
  margin-top: 4px;
  color: #778191;
  font-size: 13px;
}

.agrodefesa-upload-status {
  flex: 0 0 auto;
  padding: 5px 10px;
  border-radius: 999px;
  background: #edf2f7;
  color: #5d6675;
  font-size: 12px;
  font-weight: 600;
}

.agrodefesa-upload-box.has-file .agrodefesa-upload-status {
  background: #dff5ef;
  color: #007962;
}

.agrodefesa-file-input {
  display: none;
}

.agrodefesa-modal-cancel,
.agrodefesa-modal-submit {
  min-width: 94px;
  border-radius: 6px;
}

.agrodefesa-modal-submit {
  background: #008e75;
  border-color: #008e75;
}

.agrodefesa-modal-submit:hover,
.agrodefesa-modal-submit:focus {
  background: #007962;
  border-color: #007962;
}

.agrodefesa-log-json {
  max-width: 100%;
  max-height: 160px;
  margin: 0;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
}

.agrodefesa-failure {
  padding: 12px;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  background: #fff8f8;
}

.agrodefesa-failure + .agrodefesa-failure {
  margin-top: 10px;
}

.agrodefesa-message-list {
  margin: 0;
  padding-left: 18px;
}

.agrodefesa-message-list li + li {
  margin-top: 6px;
}

@media (max-width: 575.98px) {
  .agrodefesa-send-dialog {
    max-width: calc(100% - 20px);
    margin-left: auto;
    margin-right: auto;
  }

  .agrodefesa-upload-box {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .agrodefesa-upload-copy {
    flex-basis: calc(100% - 62px);
  }

  .agrodefesa-upload-status {
    margin-left: 62px;
  }

  .agrodefesa-send-summary {
    grid-template-columns: 1fr;
  }
}
</style>
